/**
 * The router's error element. It renders *inside* the shell, so a failed screen
 * never costs the user their navigation, and it always offers a way forward.
 */

import { Inbox, RotateCw } from 'lucide-react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

import { Button, EmptyState } from '@/components/ui';
import { ApiError } from '@/lib/api';
import { ROUTES } from '@/lib/constants';

interface Derived {
  title: string;
  description: string;
  /** Shown in mono under the copy when the server gave us a trace handle. */
  requestId: string | null;
  notFound: boolean;
}

function derive(error: unknown): Derived {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        title: 'That page does not exist',
        description: 'The address is either mistyped or the record has been removed.',
        requestId: null,
        notFound: true,
      };
    }
    return {
      title: `Request failed (${error.status})`,
      description: error.statusText || 'The route could not be loaded.',
      requestId: null,
      notFound: false,
    };
  }

  if (ApiError.is(error)) {
    return {
      title: error.isNotFound ? 'That record no longer exists' : 'This screen could not load',
      description: error.message,
      requestId: error.requestId || null,
      notFound: error.isNotFound,
    };
  }

  if (error instanceof Error) {
    return {
      title: 'This screen could not load',
      description: error.message,
      requestId: null,
      notFound: false,
    };
  }

  return {
    title: 'This screen could not load',
    description: 'An unexpected problem stopped the page from rendering.',
    requestId: null,
    notFound: false,
  };
}

export function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { title, description, requestId, notFound } = derive(error);

  return (
    <div className="flex h-full w-full items-center justify-center px-8 py-12">
      <div className="w-full max-w-[440px]">
        <EmptyState
          variant={notFound ? 'search' : 'error'}
          title={title}
          description={
            <>
              {description}
              {requestId ? (
                <span className="mt-2 block font-mono text-xs text-faint">
                  Request {requestId}
                </span>
              ) : null}
            </>
          }
          action={
            /* Navigation, not creation — so not gold. */
            <Button variant="secondary" iconLeft={<Inbox />} onClick={() => navigate(ROUTES.inbox)}>
              Go to Inbox
            </Button>
          }
          secondaryAction={
            <Button variant="ghost" iconLeft={<RotateCw />} onClick={() => navigate(0)}>
              Try again
            </Button>
          }
        />
      </div>
    </div>
  );
}
